import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Card, CardBody, Input, Button, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import AiChatLogo from "../../assets/images/ai-chat.png";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { AI_CHAT } from "../../helpers/data_helper";
import "./AiChat.css";
import RecordDescriptionModal from "../Coaching/RecordDescriptionModal";

const AiChat = () => {
  const { userInfo } = useSelector((state) => ({
    userInfo: state.UserOutletGroup.userInfo,
  }));

  const firstName = useMemo(
    () => userInfo?.first_name || userInfo?.firstName || "there",
    [userInfo],
  );

  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const [showRecordModal, setShowRecordModal] = useState(false);

  useEffect(() => {
    if (messages.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const sendMessage = async (text) => {
    const payload = {
      serviceName: AI_CHAT.serviceName,
      op: AI_CHAT.op_chat,
      message: text,
    };

    if (conversationId) {
      payload.conversation_id = conversationId;
    }

    const response = await postDataAPI(payload);
    if (response.status !== "ok") {
      throw new Error(response.message || "AI chat request failed");
    }

    if (response.data && response.data.conversation_id) {
      setConversationId(response.data.conversation_id);
    }

    return (
      response.data?.summary || response.data?.clarification_message || response.data?.message || "No response returned"
    );
  };

  const submitText = async (text) => {
    if (loading) return;
    const trimmed = (text || "").trim();
    if (!trimmed) return;

    const userMessage = { id: Date.now(), sender: "user", text: trimmed };
    const botMessage = { id: Date.now() + 1, sender: "bot", text: "Thinking..." };

    setMessages((prev) => [...prev, userMessage, botMessage]);
    setQuery("");
    setLoading(true);

    try {
      const botResponse = await sendMessage(trimmed);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessage.id ? { ...msg, text: botResponse } : msg
        )
      );
    } catch (error) {
      const errorText = error?.message || "Unable to get AI response.";
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessage.id ? { ...msg, text: errorText } : msg
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (loading) return;
    submitText(query);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center align-items-center h-100">
          <Col lg={11} xl={10}>
            <Card className="ai-chat-card shadow-none border-0">
              <CardBody className="ai-chat-card-body">
                {messages.length === 0 ? (
                  <div className="ai-chat-empty">
                    <div className="ai-chat-brand">
                      <div className="ai-chat-logo">
                        <img src={AiChatLogo} alt="AI Chat" />
                      </div>
                    </div>
                    <h1 className="ai-chat-title mb-5 text-center">
                      Hey, {firstName}. Let's dive in?
                    </h1>

                    <form
                      className="ai-chat-form"
                      onSubmit={handleSubmit}
                      // style={{ maxWidth: "600px", margin: "0 auto" }}
                    >
                      <div className="ai-chat-input-group w-100 position-relative">
                        <Input
                          className="ai-chat-input"
                          type="text"
                          value={query}
                          placeholder="Write your message..."
                          onChange={(e) => setQuery(e.target.value)}
                        />
                        <Button
                          type="button"
                          className="ai-chat-mic-btn btn-icon"
                          onClick={() => setShowRecordModal(true)}
                          disabled={loading}
                        >
                          <i className="ri-mic-line fs-5" />
                        </Button>
                        <Button
                          type="submit"
                          className="ai-chat-button btn-icon"
                          disabled={loading}
                        >
                          {loading ? <Spinner size="sm" /> : <i className="ri-send-plane-line fs-5" />}
                        </Button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="ai-chat-conversation">
                    <div className="ai-chat-dialogue">
                      <div className="ai-chat-messages">
                        {messages.map((message) => (
                          <div
                            key={message.id}
                            className={`ai-chat-message ${message.sender}`}
                          >
                            <div className="ai-chat-bubble">{message.text}</div>
                          </div>
                        ))}
                        <div ref={bottomRef} />
                      </div>
                    </div>

                    <div className="ai-chat-footer">
                      <form className="ai-chat-form" onSubmit={handleSubmit}>
                        <div className="ai-chat-input-group w-100 position-relative">
                          <Input
                            className="ai-chat-input"
                            type="text"
                            value={query}
                            placeholder="Write your message..."
                            onChange={(e) => setQuery(e.target.value)}
                          />
                          <Button
                            type="button"
                            className="ai-chat-mic-btn btn-icon"
                            onClick={() => setShowRecordModal(true)}
                            disabled={loading}
                          >
                            <i className="ri-mic-line fs-5" />
                          </Button>
                          <Button
                            type="submit"
                            className="ai-chat-button btn-icon"
                            disabled={loading}
                          >
                            {loading ? <Spinner size="sm" /> : <i className="ri-send-plane-line fs-5" />}
                          </Button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <RecordDescriptionModal
        isOpen={showRecordModal}
        onClose={() => setShowRecordModal(false)}
        onSave={(text) => {
          setShowRecordModal(false);
          // submit the transcribed text directly into chat
          submitText(text);
        }}
        dishName={"Chat"}
      />
    </div>
  );
};

export default AiChat;
